import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Button
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Info as InfoIcon,
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { API_URL } from '../../../../config';
import { mockAuthHeaders } from '../../../../utils/mockAuth';

/**
 * EvidenceCircles - Visual representation of selected vs target evidence
 * Shows filled circles for selected items, empty circles for remaining slots
 */
const EvidenceCircles = ({ selected, targetMin, targetMax, available }) => {
  const numCircles = targetMax || available || 5;
  const filledCircles = Math.min(selected, numCircles);
  const emptyCircles = numCircles - filledCircles;
  const overflow = selected > numCircles ? selected - numCircles : 0;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {/* Filled circles */}
      {Array(filledCircles).fill().map((_, i) => (
        <Box
          key={`filled-${i}`}
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            border: '1px solid',
            borderColor: 'primary.dark'
          }}
        />
      ))}
      
      {/* Empty circles */}
      {Array(emptyCircles).fill().map((_, i) => (
        <Box
          key={`empty-${i}`}
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            bgcolor: 'grey.200',
            border: '1px solid',
            borderColor: 'grey.400'
          }}
        />
      ))}
      
      {/* Overflow indicator */}
      {overflow > 0 && (
        <Chip 
          label={`+${overflow}`} 
          size="small" 
          color="primary"
          sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }}
        />
      )}
    </Stack>
  );
};

/**
 * CriterionStatusCircle - Single circle showing status with color coding
 * Used in overall coverage section
 */
const CriterionStatusCircle = ({ selected, targetMin, targetMax, available, size = 'small', criterionText }) => {
  const getCircleColor = () => {
    if (available === 0) return 'grey.400';           // No evidence
    if (selected === 0) return 'grey.400';            // Not started
    if (selected < targetMin) return 'error.main';    // Below minimum (Red)
    if (selected >= targetMin && selected < targetMax) return 'warning.main';  // At minimum (Yellow)
    return 'success.main';                            // At/above maximum (Green)
  };

  const getTooltip = () => {
    const status = selected >= targetMax ? 'Complete' :
                   selected >= targetMin ? 'At minimum' :
                   selected > 0 ? 'Below target' : 'No evidence';
    return `${criterionText}: ${selected}/${targetMin}-${targetMax} (${available} available) - ${status}`;
  };

  const circleSize = size === 'small' ? 18 : 24;

  return (
    <Tooltip title={getTooltip()} arrow>
      <Box
        sx={{
          width: circleSize,
          height: circleSize,
          borderRadius: '50%',
          bgcolor: getCircleColor(),
          border: '2px solid',
          borderColor: 'divider',
          cursor: 'help'
        }}
      />
    </Tooltip>
  );
};

/**
 * CriteriaTab - Display job requirements with coverage indicators
 * 
 * Shows:
 * - Critical/High/Medium/Low importance requirements
 * - Evidence from resume match analysis
 * - Coverage indicators (Exceeds/Meets/Partial/Missing)
 * - Overall fit score
 * 
 * Uses existing match analysis data from Agent 2
 */
const CriteriaTab = ({ jobDescription, selectedItems, graphData, onToggleSelection }) => {
  // Use graph data nodes directly
  const criteriaNodes = graphData?.criteriaNodes || [];
  const profileNodes = graphData?.profileNodes || [];
  const matchingGraph = graphData?.matchingGraph;
  
  // State for UI
  const [reEvaluating, setReEvaluating] = useState(false);
  const [expandedCritical, setExpandedCritical] = useState(false);
  const [expandedRequired, setExpandedRequired] = useState(false);
  const [expandedWanted, setExpandedWanted] = useState(false);
  const [expandedNiceToHave, setExpandedNiceToHave] = useState(false);
  const [expandedCriteria, setExpandedCriteria] = useState({});
  
  // Handler to open enhancement flow in new tab with criterion context
  const handleEnhanceProfile = async (criterion) => {
    try {
      // Create conversation with criterion context
      const response = await fetch(`${API_URL}/api/profile/enhancement/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...mockAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          topic: `Evidence for: ${criterion.text}`,
          context: {
            criterionId: criterion.id || '',
            criterionText: criterion.text,
            source: 'resume_builder'
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create conversation');
      }

      const data = await response.json();
      
      // Navigate to enhancement flow (same tab)
      const url = `/enhancement?conversationId=${data.conversation_id}`;
      window.location.href = url;
      
    } catch (error) {
      console.error('Error creating enhancement conversation:', error);
      alert('Failed to start enhancement conversation. Please try again.');
    }
  };

  // Group criteria by type
  const requiredItems = criteriaNodes.filter(c => c.type === 'required');
  const criticalItems = criteriaNodes.filter(c => c.type === 'critical');
  const wantedItems = criteriaNodes.filter(c => c.type === 'wanted');
  const niceToHaveItems = criteriaNodes.filter(c => c.type === 'nice_to_have');

  // Re-evaluate triggers reload at parent level
  // (Simplified - parent will reload and re-integrate data)

  const handleReEvaluateAll = async () => {
    if (!jobDescription?.application_id) return;
    
    try {
      setReEvaluating(true);
      
      // Trigger categorical matching
      const response = await fetch(
        `${API_URL}/api/automation/match-profile-categorical`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...mockAuthHeaders()
          },
          credentials: 'include',
          body: JSON.stringify({
            application_id: jobDescription.application_id,
            force_rematch: true
          })
        }
      );
      
      if (response.ok) {
        alert('Matching re-evaluation started. Reload the page in a moment to see updated results.');
      } else {
        console.error('[CriteriaTab] Re-evaluation failed:', response.status);
        alert('Failed to re-evaluate criteria. Please try again.');
      }
    } catch (error) {
      console.error('[CriteriaTab] Error re-evaluating:', error);
      alert('Error re-evaluating criteria. Please try again.');
    } finally {
      setReEvaluating(false);
    }
  };

  // Get matched profile items for a specific criterion
  // Uses pre-calculated data from graph adapter
  const getMatchedItems = (criterionNode) => {
    if (!criterionNode?._matchingInfo) {
      return [];
    }
    
    // Get edges and look up full profile nodes
    const edges = criterionNode._matchingInfo.edges || [];
    
    return edges.map(edge => {
      // Use item_id for lookup (graphDataAdapter now uses item_id)
      const profileNode = profileNodes.find(n => n.item_id === edge.from || n.id === edge.from);
      
      return {
        item_id: edge.from,
        clarity: edge.clarity,
        strength: edge.strength,
        snippet: edge.snippet,
        text: edge.snippet,
        // Add full item data from profile node
        item_type: profileNode?.type,
        company: profileNode?.company,
        title: profileNode?.title,
        dates: profileNode?.dates,
        _isSelected: profileNode?._isSelected,
        content: profileNode ? {
          company: profileNode.company,
          title: profileNode.title
        } : null
      };
    });
  };

  // Parse target evidence string like "3-5 pieces" or "1-2 pieces" 
  const parseTargetEvidence = (targetStr) => {
    if (!targetStr) return { min: 0, max: 0 };
    const match = targetStr.match(/(\d+)(?:-(\d+))?/);
    if (!match) return { min: 0, max: 0 };
    const min = parseInt(match[1]);
    const max = match[2] ? parseInt(match[2]) : min;
    return { min, max };
  };

  // Get coverage display with visual circles
  const getCoverageDisplay = (criterionNode) => {
    const coverage = criterionNode._coverage || { available: 0, selected: 0 };
    const target = parseTargetEvidence(criterionNode.target_evidence);
    
    return {
      circles: (
        <EvidenceCircles
          selected={coverage.selected}
          targetMin={target.min}
          targetMax={target.max}
          available={coverage.available}
        />
      ),
      availableChip: coverage.available > 0 ? (
        <Chip
          size="small"
          label={`${coverage.available} available`}
          variant="outlined"
          color={coverage.selected > 0 ? 'primary' : 'default'}
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
      ) : (
        <Chip
          size="small"
          label="No evidence"
          variant="outlined"
          color="error"
          icon={<CancelIcon sx={{ fontSize: '0.75rem' }} />}
          sx={{ height: 20, fontSize: '0.65rem' }}
        />
      )
    };
  };

  // OLD: Removed - now using criteriaNodes from graphData directly
  // which are already extracted and annotated with matching info
  

  // Calculate overall coverage stats
  const calculateOverallStats = () => {
    const totalCriteria = criteriaNodes.length;
    if (totalCriteria === 0) return { covered: 0, total: 0, percentage: 0 };
    
    // Count criteria that have at least one available match
    const coveredCriteria = criteriaNodes.filter(c => 
      c._matchingInfo && c._matchingInfo.count > 0
    ).length;
    
    return {
      covered: coveredCriteria,
      total: totalCriteria,
      percentage: Math.round((coveredCriteria / totalCriteria) * 100)
    };
  };

  const overallStats = matchingGraph ? calculateOverallStats() : { covered: 0, total: 0, percentage: 0 };

  // No loading state needed - data comes from parent

  return (
    <Box>
      {/* Header with Re-evaluate All Button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" component="h2">
          Job Requirements
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={reEvaluating ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleReEvaluateAll}
          disabled={reEvaluating || !jobDescription?.application_id}
        >
          {reEvaluating ? 'Re-evaluating...' : 'Re-evaluate All Criteria'}
        </Button>
      </Stack>

      {/* Debug removed - using criteriaNodes from graphData */}

      {/* Criteria Exists But Empty */}
      {criteriaNodes.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium">
            No criteria found
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            No requirements have been extracted from the job description yet.
          </Typography>
        </Alert>
      )}

      {/* Matching Status Messages */}
      {!matchingGraph && criteriaNodes.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="medium">
            Profile matching not yet run
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Click "Re-evaluate All Criteria" to match your profile against these requirements.
          </Typography>
        </Alert>
      )}

      {/* Overall Coverage */}
      {matchingGraph && criteriaNodes.length > 0 && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" fontWeight="bold" gutterBottom display="block" sx={{ mb: 1.5 }}>
            Overall Coverage
          </Typography>
          
          {/* Required */}
          {requiredItems.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ minWidth: 120, fontSize: '0.7rem' }}>
                  ⭐ REQUIRED ({requiredItems.length})
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  {requiredItems.map((item, idx) => {
                    const coverage = item._coverage || { available: 0, selected: 0 };
                    const target = parseTargetEvidence(item.target_evidence);
                    return (
                      <CriterionStatusCircle
                        key={idx}
                        selected={coverage.selected}
                        targetMin={target.min || 1}
                        targetMax={target.max || coverage.available || 1}
                        available={coverage.available}
                        size="small"
                        criterionText={item.text}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            </Box>
          )}
          
          {/* Critical */}
          {criticalItems.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ minWidth: 120, fontSize: '0.7rem' }}>
                  🔴 CRITICAL ({criticalItems.length})
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  {criticalItems.map((item, idx) => {
                    const coverage = item._coverage || { available: 0, selected: 0 };
                    const target = parseTargetEvidence(item.target_evidence);
                    return (
                      <CriterionStatusCircle
                        key={idx}
                        selected={coverage.selected}
                        targetMin={target.min || 1}
                        targetMax={target.max || coverage.available || 1}
                        available={coverage.available}
                        size="small"
                        criterionText={item.text}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            </Box>
          )}
          
          {/* Wanted */}
          {wantedItems.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ minWidth: 120, fontSize: '0.7rem' }}>
                  🟡 WANTED ({wantedItems.length})
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  {wantedItems.map((item, idx) => {
                    const coverage = item._coverage || { available: 0, selected: 0 };
                    const target = parseTargetEvidence(item.target_evidence);
                    return (
                      <CriterionStatusCircle
                        key={idx}
                        selected={coverage.selected}
                        targetMin={target.min || 1}
                        targetMax={target.max || coverage.available || 1}
                        available={coverage.available}
                        size="small"
                        criterionText={item.text}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            </Box>
          )}
          
          {/* Nice to Have */}
          {niceToHaveItems.length > 0 && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" sx={{ minWidth: 120, fontSize: '0.7rem' }}>
                  💡 NICE TO HAVE ({niceToHaveItems.length})
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  {niceToHaveItems.map((item, idx) => {
                    const coverage = item._coverage || { available: 0, selected: 0 };
                    const target = parseTargetEvidence(item.target_evidence);
                    return (
                      <CriterionStatusCircle
                        key={idx}
                        selected={coverage.selected}
                        targetMin={target.min || 1}
                        targetMax={target.max || coverage.available || 1}
                        available={coverage.available}
                        size="small"
                        criterionText={item.text}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Criteria Sections - Only show if criteria exists */}
      {criteriaNodes.length > 0 && (
        <Box>
          {/* Required - ALWAYS show */}
          <Box mb={2}>
            <Typography variant="caption" fontWeight="bold" color="error.main" gutterBottom display="block">
              ⭐ REQUIRED ({requiredItems.length})
            </Typography>
            {requiredItems.length === 0 ? (
              <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                No required items identified
              </Alert>
            ) : (
              requiredItems.map((item, index) => {
                const matchedItems = getMatchedItems(item);
                const coverageDisplay = getCoverageDisplay(item);
                
                return (
                  <Accordion
                    key={index}
                    elevation={0}
                    sx={{ border: '1px solid', borderColor: 'divider', mb: 1 }}
                    expanded={expandedCriteria[`required-${index}`] || false}
                    onChange={() => setExpandedCriteria(prev => ({
                      ...prev,
                      [`required-${index}`]: !prev[`required-${index}`]
                    }))}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box flex={1}>
                        <Typography variant="caption" display="block">
                          {item.text}
                        </Typography>
                        <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
                          {coverageDisplay.circles}
                          {coverageDisplay.availableChip}
                        </Stack>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {matchedItems.length > 0 ? (
                        <Box>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="caption" fontWeight="bold" display="block">
                              Matched Profile Items ({matchedItems.length}):
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AutoAwesomeIcon />}
                              onClick={() => handleEnhanceProfile(item)}
                              sx={{ fontSize: '0.65rem', height: 24 }}
                            >
                              Enhance Profile
                            </Button>
                          </Stack>
                          {matchedItems.map((matchedItem, itemIndex) => (
                            <Paper
                              key={itemIndex}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                mb: 1,
                                bgcolor: matchedItem._isSelected ? 'success.50' : 'grey.50',
                                border: '1px solid',
                                borderColor: matchedItem._isSelected ? 'success.200' : 'divider'
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                {/* Selection Checkbox */}
                                {onToggleSelection && (
                                  <input
                                    type="checkbox"
                                    checked={matchedItem._isSelected || false}
                                    onChange={() => onToggleSelection(matchedItem.item_id)}
                                    style={{ marginTop: '2px', cursor: 'pointer' }}
                                  />
                                )}
                                <Box flex={1}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                    <Typography variant="caption" fontWeight="medium" display="block">
                                      {matchedItem.item_type === 'accomplishment' || matchedItem.item_type === 'experience' 
                                        ? `${matchedItem.company || matchedItem.content?.company || 'Experience'} - ${matchedItem.title || matchedItem.content?.title || 'Position'}` 
                                        : matchedItem.item_type === 'highlight'
                                        ? 'Key Highlight'
                                        : matchedItem.item_type === 'project'
                                        ? matchedItem.title || 'Project'
                                        : 'Profile Item'}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={`${matchedItem.strength || 'medium'} · ${matchedItem.clarity || 'implicit'}`}
                                      color={matchedItem.strength === 'strong' ? 'success' : matchedItem.strength === 'medium' ? 'warning' : 'default'}
                                      sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                                    />
                                  </Stack>
                                  {matchedItem.snippet && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                      {matchedItem.snippet}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </Paper>
                          ))}
                        </Box>
                      ) : (
                        <Box>
                          <Alert severity="warning" sx={{ fontSize: '0.75rem', mb: 1.5 }}>
                            No matching profile items found. Consider adding relevant experience to your profile.
                          </Alert>
                          <Button
                            variant="contained"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => handleEnhanceProfile(item)}
                            fullWidth
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Enhance Profile for This Requirement
                          </Button>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Box>

          {/* Critical - ALWAYS show */}
          <Box mb={2}>
            <Typography variant="caption" fontWeight="bold" color="error.main" gutterBottom display="block">
              🔴 CRITICAL ({criticalItems.length})
            </Typography>
            {criticalItems.length === 0 ? (
              <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                No critical requirements identified
              </Alert>
            ) : (
              criticalItems.map((item, index) => {
                const matchedItems = getMatchedItems(item);
                const coverageDisplay = getCoverageDisplay(item);
                
                return (
                  <Accordion
                    key={index}
                    elevation={0}
                    sx={{ border: '1px solid', borderColor: 'divider', mb: 1 }}
                    expanded={expandedCriteria[`critical-${index}`] || false}
                    onChange={() => setExpandedCriteria(prev => ({
                      ...prev,
                      [`critical-${index}`]: !prev[`critical-${index}`]
                    }))}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box flex={1}>
                        <Typography variant="caption" display="block">
                          {item.text}
                        </Typography>
                        <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
                          {coverageDisplay.circles}
                          {coverageDisplay.availableChip}
                        </Stack>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {matchedItems.length > 0 ? (
                        <Box>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="caption" fontWeight="bold" display="block">
                              Matched Profile Items ({matchedItems.length}):
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AutoAwesomeIcon />}
                              onClick={() => handleEnhanceProfile(item)}
                              sx={{ fontSize: '0.65rem', height: 24 }}
                            >
                              Enhance Profile
                            </Button>
                          </Stack>
                          {matchedItems.map((matchedItem, itemIndex) => (
                            <Paper
                              key={itemIndex}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                mb: 1,
                                bgcolor: matchedItem._isSelected ? 'success.50' : 'grey.50',
                                border: '1px solid',
                                borderColor: matchedItem._isSelected ? 'success.200' : 'divider'
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                {/* Selection Checkbox */}
                                {onToggleSelection && (
                                  <input
                                    type="checkbox"
                                    checked={matchedItem._isSelected || false}
                                    onChange={() => onToggleSelection(matchedItem.item_id)}
                                    style={{ marginTop: '2px', cursor: 'pointer' }}
                                  />
                                )}
                                <Box flex={1}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                    <Typography variant="caption" fontWeight="medium" display="block">
                                      {matchedItem.item_type === 'accomplishment' || matchedItem.item_type === 'experience' 
                                        ? `${matchedItem.company || matchedItem.content?.company || 'Experience'} - ${matchedItem.title || matchedItem.content?.title || 'Position'}` 
                                        : matchedItem.item_type === 'highlight'
                                        ? 'Key Highlight'
                                        : matchedItem.item_type === 'project'
                                        ? matchedItem.title || 'Project'
                                        : 'Profile Item'}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={`${matchedItem.strength || 'medium'} · ${matchedItem.clarity || 'implicit'}`}
                                      color={matchedItem.strength === 'strong' ? 'success' : matchedItem.strength === 'medium' ? 'warning' : 'default'}
                                      sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                                    />
                                  </Stack>
                                  {matchedItem.snippet && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                      {matchedItem.snippet}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </Paper>
                          ))}
                        </Box>
                      ) : (
                        <Box>
                          <Alert severity="warning" sx={{ fontSize: '0.75rem', mb: 1.5 }}>
                            No matching profile items found. Consider adding relevant experience to your profile.
                          </Alert>
                          <Button
                            variant="contained"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => handleEnhanceProfile(item)}
                            fullWidth
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Enhance Profile for This Requirement
                          </Button>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Box>

          {/* Wanted - ALWAYS show */}
          <Box mb={2}>
            <Typography variant="caption" fontWeight="bold" color="warning.dark" gutterBottom display="block">
              🟡 WANTED ({wantedItems.length})
            </Typography>
            {wantedItems.length === 0 ? (
              <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                No wanted items identified
              </Alert>
            ) : (
              wantedItems.map((item, index) => {
                const matchedItems = getMatchedItems(item);
                const coverageDisplay = getCoverageDisplay(item);
                
                return (
                  <Accordion
                    key={index}
                    elevation={0}
                    sx={{ border: '1px solid', borderColor: 'divider', mb: 1 }}
                    expanded={expandedCriteria[`wanted-${index}`] || false}
                    onChange={() => setExpandedCriteria(prev => ({
                      ...prev,
                      [`wanted-${index}`]: !prev[`wanted-${index}`]
                    }))}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box flex={1}>
                        <Typography variant="caption" display="block">
                          {item.text}
                        </Typography>
                        <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
                          {coverageDisplay.circles}
                          {coverageDisplay.availableChip}
                        </Stack>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {matchedItems.length > 0 ? (
                        <Box>
                          {matchedItems.map((matchedItem, mIndex) => (
                            <Paper
                              key={mIndex}
                              elevation={0}
                              sx={{
                                p: 1,
                                mb: 1,
                                bgcolor: matchedItem._isSelected ? 'success.50' : 'grey.50',
                                border: '1px solid',
                                borderColor: matchedItem._isSelected ? 'success.200' : 'divider'
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                {onToggleSelection && (
                                  <input
                                    type="checkbox"
                                    checked={matchedItem._isSelected || false}
                                    onChange={() => onToggleSelection(matchedItem.item_id)}
                                    style={{ marginTop: '2px', cursor: 'pointer' }}
                                  />
                                )}
                                <Box flex={1}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                    <Typography variant="caption" fontWeight="medium" display="block">
                                      {matchedItem.item_type === 'accomplishment' || matchedItem.item_type === 'experience' 
                                        ? `${matchedItem.company || matchedItem.content?.company || 'Experience'} - ${matchedItem.title || matchedItem.content?.title || 'Position'}` 
                                        : matchedItem.item_type === 'highlight'
                                        ? 'Key Highlight'
                                        : matchedItem.item_type === 'project'
                                        ? matchedItem.title || 'Project'
                                        : 'Profile Item'}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={`${matchedItem.strength || 'medium'} · ${matchedItem.clarity || 'implicit'}`}
                                      color={matchedItem.strength === 'strong' ? 'success' : matchedItem.strength === 'medium' ? 'warning' : 'default'}
                                      sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                                    />
                                  </Stack>
                                  {matchedItem.snippet && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                      {matchedItem.snippet}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </Paper>
                          ))}
                        </Box>
                      ) : (
                        <Box>
                          <Alert severity="warning" sx={{ fontSize: '0.75rem', mb: 1.5 }}>
                            No matching profile items found. Consider adding relevant experience to your profile.
                          </Alert>
                          <Button
                            variant="contained"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => handleEnhanceProfile(item)}
                            fullWidth
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Enhance Profile for This Requirement
                          </Button>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Box>

          {/* Nice to Have - ALWAYS show */}
          <Box mb={2}>
            <Typography variant="caption" fontWeight="bold" color="info.main" gutterBottom display="block">
              💡 NICE TO HAVE ({niceToHaveItems.length})
            </Typography>
            {niceToHaveItems.length === 0 ? (
              <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                No nice-to-have items identified
              </Alert>
            ) : (
              niceToHaveItems.map((item, index) => {
                const matchedItems = getMatchedItems(item);
                const coverageDisplay = getCoverageDisplay(item);
                
                return (
                  <Accordion
                    key={index}
                    elevation={0}
                    sx={{ border: '1px solid', borderColor: 'divider', mb: 1 }}
                    expanded={expandedCriteria[`nice-${index}`] || false}
                    onChange={() => setExpandedCriteria(prev => ({
                      ...prev,
                      [`nice-${index}`]: !prev[`nice-${index}`]
                    }))}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box flex={1}>
                        <Typography variant="caption" display="block">
                          {item.text}
                        </Typography>
                        <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
                          {coverageDisplay.circles}
                          {coverageDisplay.availableChip}
                        </Stack>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {matchedItems.length > 0 ? (
                        <Box>
                          {matchedItems.map((matchedItem, mIndex) => (
                            <Paper
                              key={mIndex}
                              elevation={0}
                              sx={{
                                p: 1,
                                mb: 1,
                                bgcolor: matchedItem._isSelected ? 'success.50' : 'grey.50',
                                border: '1px solid',
                                borderColor: matchedItem._isSelected ? 'success.200' : 'divider'
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="flex-start">
                                {onToggleSelection && (
                                  <input
                                    type="checkbox"
                                    checked={matchedItem._isSelected || false}
                                    onChange={() => onToggleSelection(matchedItem.item_id)}
                                    style={{ marginTop: '2px', cursor: 'pointer' }}
                                  />
                                )}
                                <Box flex={1}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                    <Typography variant="caption" fontWeight="medium" display="block">
                                      {matchedItem.item_type === 'accomplishment' || matchedItem.item_type === 'experience' 
                                        ? `${matchedItem.company || matchedItem.content?.company || 'Experience'} - ${matchedItem.title || matchedItem.content?.title || 'Position'}` 
                                        : matchedItem.item_type === 'highlight'
                                        ? 'Key Highlight'
                                        : matchedItem.item_type === 'project'
                                        ? matchedItem.title || 'Project'
                                        : 'Profile Item'}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={`${matchedItem.strength || 'medium'} · ${matchedItem.clarity || 'implicit'}`}
                                      color={matchedItem.strength === 'strong' ? 'success' : matchedItem.strength === 'medium' ? 'warning' : 'default'}
                                      sx={{ height: 18, fontSize: '0.65rem', textTransform: 'capitalize' }}
                                    />
                                  </Stack>
                                  {matchedItem.snippet && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                                      {matchedItem.snippet}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </Paper>
                          ))}
                        </Box>
                      ) : (
                        <Box>
                          <Alert severity="warning" sx={{ fontSize: '0.75rem', mb: 1.5 }}>
                            No matching profile items found. Consider adding relevant experience to your profile.
                          </Alert>
                          <Button
                            variant="contained"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => handleEnhanceProfile(item)}
                            fullWidth
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Enhance Profile for This Requirement
                          </Button>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CriteriaTab;
